import { registerSiteContent } from '@/features/siteContent/runtime';

/**
 * Copy for every `/features/:slug` page. Keyed by the slugs declared in
 * `../nav.ts`; the FeaturePage looks the slug up in both and 404s if either
 * side is missing, which keeps menu and pages honest with each other.
 */

export interface FeatureContent {
  /** Page headline - benefit-first, not the feature's name. */
  title: string;
  lede: string;
  /** Three quick proof points under the headline. */
  bullets: string[];
  /** Three deeper capability cards. */
  benefits: { title: string; description: string }[];
}

export const FEATURE_CONTENT: Record<string, FeatureContent> = {
  'store-builder': {
    title: 'Build a store you are proud of - without writing code',
    lede: 'Pick a theme, add your logo and colours, and publish. Your storefront is live on your own subdomain in minutes, and every change you make appears instantly.',
    bullets: [
      'Live on your own subdomain in about 10 minutes',
      'Edit sections, banners and pages from the dashboard',
      'Mobile-first layouts out of the box',
    ],
    benefits: [
      {
        title: 'Sections, not source code',
        description:
          'Arrange hero banners, product grids and highlights the way you arrange a shelf. What you see in the editor is exactly what shoppers get.',
      },
      {
        title: 'Fast by default',
        description:
          'Pages are optimised and served from fast infrastructure, so your store loads quickly even on patchy mobile networks.',
      },
      {
        title: 'Grows with you',
        description:
          'Start with one page and a handful of products; add categories, policies and custom pages as your catalogue grows.',
      },
    ],
  },
  themes: {
    title: 'Designs that make small brands look big',
    lede: 'Professionally designed themes tuned for Indian D2C brands - clean typography, strong product imagery and layouts that convert on mobile.',
    bullets: [
      'Switch themes without losing your content',
      'Customise colours, fonts and layout per theme',
      'Every theme is responsive and accessibility-checked',
    ],
    benefits: [
      {
        title: 'Your brand, front and centre',
        description:
          'Logo, palette and typography apply across the storefront, cart and checkout, so the experience feels like one brand end to end.',
      },
      {
        title: 'Built for conversion',
        description:
          'Product pages lead with photos, price and a clear call to action - patterns proven across thousands of mobile-first stores.',
      },
      {
        title: 'No redesign tax',
        description:
          'Try a new look in preview and publish when ready. Products, pages and settings carry over automatically.',
      },
    ],
  },
  'custom-domains': {
    title: 'Sell on your own domain',
    lede: 'Connect the .in or .com you already own, or start on a free NeonCarts subdomain and upgrade when you are ready. HTTPS is handled for you.',
    bullets: [
      'Free yourstore.neoncarts.com subdomain on every plan',
      'Connect your own domain on Growth and above',
      'Automatic SSL certificates, renewed for you',
    ],
    benefits: [
      {
        title: 'One store, your address',
        description:
          'Shoppers see your brand in the address bar, on invoices and in every WhatsApp message - not a marketplace URL you rent.',
      },
      {
        title: 'Simple to connect',
        description:
          'Point your DNS with a guided checklist. Most domains verify within minutes, with no downtime for a store that is already live.',
      },
      {
        title: 'Secure by default',
        description:
          'Certificates are issued and renewed automatically, so your checkout always shows the padlock customers look for.',
      },
    ],
  },
  'customer-accounts': {
    title: 'Give shoppers a reason to come back',
    lede: 'Customer accounts keep addresses, order history and support in one place - so a repeat purchase takes seconds, not another form.',
    bullets: [
      'One-tap sign-in for returning customers',
      'Saved addresses speed up every checkout',
      'Order history and status without a support ping',
    ],
    benefits: [
      {
        title: 'Faster repeat checkout',
        description:
          'Returning shoppers skip the address form entirely. Fewer fields means fewer drop-offs on the payment step.',
      },
      {
        title: 'Self-serve order tracking',
        description:
          '"Where is my order?" answers itself - customers see live status and history from their account page.',
      },
      {
        title: 'A customer list you own',
        description:
          'Every account enriches your customer directory, powering segments for discounts and WhatsApp campaigns.',
      },
    ],
  },
  'ai-assistant': {
    title: 'An AI assistant that works your storefront',
    lede: 'Draft product descriptions, SEO titles and campaign copy in seconds - trained on commerce and grounded in your actual catalogue.',
    bullets: [
      'Product copy and SEO metadata in one click',
      'Campaign and support drafts in your tone',
      'Clear monthly usage included on every plan',
    ],
    benefits: [
      {
        title: 'Listings that sell themselves',
        description:
          'Turn a product name and a photo into a complete listing - description, highlights and search-friendly metadata.',
      },
      {
        title: 'Marketing without a writer',
        description:
          'Generate WhatsApp broadcast copy and offer announcements that match your brand voice, ready to review and send.',
      },
      {
        title: 'You stay in control',
        description:
          'Everything the assistant writes is a draft until you approve it, and plan-based limits keep usage predictable.',
      },
    ],
  },
  'online-store': {
    title: 'Your store, open 24×7',
    lede: 'A fast, mobile-first storefront that takes orders while you sleep - complete with cart, checkout, payments and automatic order confirmations.',
    bullets: [
      'Storefront, cart and checkout included',
      'Optimised for the mobile shoppers who make up most of Indian e-commerce',
      'Live platform status you can verify anytime',
    ],
    benefits: [
      {
        title: 'Everything connected',
        description:
          'Catalogue, stock, orders and payments share one source of truth - sell an item and inventory updates everywhere at once.',
      },
      {
        title: 'Built to stay up',
        description:
          'Monitored infrastructure with public health checks means your store is selling even when you are not watching.',
      },
      {
        title: 'Found on Google',
        description:
          'Clean URLs, fast pages and editable SEO metadata help shoppers find you through search, not just your links.',
      },
    ],
  },
  'whatsapp-commerce': {
    title: 'Turn WhatsApp chats into orders',
    lede: 'Sell on the official WhatsApp Business API - share your catalogue, confirm orders and send delivery updates inside the app your customers already use.',
    bullets: [
      'Official Business API, not a grey-market gateway',
      'Order confirmations and shipping updates, automated',
      'Approved message templates managed for you',
    ],
    benefits: [
      {
        title: 'Meet customers where they are',
        description:
          'Share products and your store link in chat. Customers browse, pay and track without leaving WhatsApp.',
      },
      {
        title: 'Automatic order updates',
        description:
          'Confirmations, payment receipts and delivery notifications go out on time, every time - no manual follow-ups.',
      },
      {
        title: 'Safe and compliant',
        description:
          'Verified business messaging on the official API protects your number and keeps deliverability high.',
      },
    ],
  },
  'social-selling': {
    title: 'Sell where your followers already scroll',
    lede: 'Turn Instagram and Facebook attention into orders - share shoppable links, run drops in stories and route everything to one checkout.',
    bullets: [
      'Shoppable store links for bios, posts and stories',
      'One catalogue behind every channel',
      'Orders land in the same dashboard, wherever they start',
    ],
    benefits: [
      {
        title: 'From post to purchase',
        description:
          'Every product has a share-ready link, so a story mention or reel can carry a shopper straight to checkout.',
      },
      {
        title: 'No double bookkeeping',
        description:
          'Social orders use the same inventory and invoicing as your website - nothing to reconcile at the end of the day.',
      },
      {
        title: 'Ready for marketplaces',
        description:
          'Keep one catalogue as your source of truth as you expand to new channels, without re-entering products.',
      },
    ],
  },
  'share-links': {
    title: 'A shop link for every conversation',
    lede: 'Product links and QR codes that open straight to checkout - perfect for WhatsApp chats, visiting cards, packaging and shop counters.',
    bullets: [
      'Instant short links for your store and every product',
      'Downloadable QR codes for print and packaging',
      'Track which links bring you orders',
    ],
    benefits: [
      {
        title: 'Close the sale in chat',
        description:
          'Answer an enquiry with a product link that carries photos, price and a working buy button - not a screenshot.',
      },
      {
        title: 'Bridge offline to online',
        description:
          'A QR on your counter, flyer or parcel turns a walk-in or a delivery into a repeat online customer.',
      },
      {
        title: 'Know what works',
        description:
          'See which links and codes convert so you invest in the channels that actually bring orders.',
      },
    ],
  },
  orders: {
    title: 'Every order under control, from placed to delivered',
    lede: 'One timeline per order - items, payment, shipment and invoice together. Update a status and the customer hears about it automatically.',
    bullets: [
      'Orders from every channel in one queue',
      'GST-compliant invoice generated for every order',
      'Status changes notify customers automatically',
    ],
    benefits: [
      {
        title: 'A single order timeline',
        description:
          'Payment, packing, dispatch and delivery live on one screen, so anyone on your team can answer "what happened to order #1248?"',
      },
      {
        title: 'Server-side accuracy',
        description:
          'Totals, taxes and discounts are computed on the server - what the customer paid is always what the books say.',
      },
      {
        title: 'Returns without chaos',
        description:
          'Cancellations and refunds follow a clear flow that restocks inventory and keeps invoices consistent.',
      },
    ],
  },
  inventory: {
    title: 'Stock that never drifts',
    lede: 'Inventory updates with every sale, restock and return - across your website, WhatsApp and social channels - so you never sell what you do not have.',
    bullets: [
      'Live stock counts across all channels',
      'Variant-level tracking for size, colour and more',
      'Low-stock alerts before you run out',
    ],
    benefits: [
      {
        title: 'One count, everywhere',
        description:
          'A sale on any channel decrements the same stock ledger instantly - no oversold orders, no spreadsheet reconciliation.',
      },
      {
        title: 'Movements you can audit',
        description:
          'Every change is a recorded movement with a reason - sale, restock, return or correction - so counts always add up.',
      },
      {
        title: 'Restock at the right time',
        description:
          'Low-stock thresholds per product flag what needs reordering before a bestseller goes dark.',
      },
    ],
  },
  shipping: {
    title: 'Shipping that keeps its promises',
    lede: 'Compare rates, print labels and share live tracking - through pluggable courier integrations that grow with your delivery footprint.',
    bullets: [
      'Rates and labels from integrated couriers',
      'Tracking links shared with customers automatically',
      'COD and prepaid handled the same way',
    ],
    benefits: [
      {
        title: 'Pick the right courier',
        description:
          'Compare serviceability and rates per shipment instead of committing everything to one partner.',
      },
      {
        title: 'Fewer "where is it?" messages',
        description:
          'Tracking updates flow to customers on WhatsApp and email the moment the courier scans the parcel.',
      },
      {
        title: 'COD without the risk drama',
        description:
          'Cash-on-delivery orders follow the same pipeline with clear reconciliation when the courier remits.',
      },
    ],
  },
  customers: {
    title: 'Know your customers, not just your orders',
    lede: 'A living directory of everyone who buys from you - their orders, value and preferences - ready to power your next offer.',
    bullets: [
      'Profiles built automatically from every order',
      'Purchase history and lifetime value per customer',
      'Segments ready for discounts and campaigns',
    ],
    benefits: [
      {
        title: 'The full picture per person',
        description:
          'Contact details, addresses, every order and every conversation-worthy note in one profile.',
      },
      {
        title: 'Find your best buyers',
        description:
          'Sort by spend, recency or frequency to see who deserves early access to your next drop.',
      },
      {
        title: 'Reach them where it counts',
        description:
          'Turn any segment into a WhatsApp campaign audience or a targeted discount in a couple of clicks.',
      },
    ],
  },
  discounts: {
    title: 'Offers that move stock, not just margins',
    lede: 'Coupon codes, automatic discounts and time-boxed sales - with the guardrails that stop a promo from going viral at your expense.',
    bullets: [
      'Percentage, flat and free-shipping discounts',
      'Usage limits, minimum carts and expiry built in',
      'Performance tracked per code',
    ],
    benefits: [
      {
        title: 'Launch a sale in minutes',
        description:
          'Create a code, set the rules and share it on WhatsApp - the checkout enforces every condition automatically.',
      },
      {
        title: 'Guardrails included',
        description:
          'Caps per customer, minimum order values and end dates mean no unpleasant surprises in the morning report.',
      },
      {
        title: 'Learn what converts',
        description:
          'See revenue and redemptions per code, so the next festival sale starts from data, not guesswork.',
      },
    ],
  },
  'whatsapp-campaigns': {
    title: 'Broadcasts that feel personal, at scale',
    lede: 'Send offers, restock alerts and festival greetings to opted-in customers on the official WhatsApp Business API - with templates, segments and results in one place.',
    bullets: [
      'Approved templates on the official API',
      'Audience segments from your customer data',
      'Delivery and response tracking per campaign',
    ],
    benefits: [
      {
        title: 'The channel Indians actually open',
        description:
          'WhatsApp messages get seen in minutes, not the next day. Announcements land where your customers already talk to you.',
      },
      {
        title: 'Right message, right people',
        description:
          'Target repeat buyers, lapsed customers or a city - segments come straight from your order history.',
      },
      {
        title: 'Compliant by construction',
        description:
          'Opt-in management and template approval are built into the flow, protecting your sender quality.',
      },
    ],
  },
  analytics: {
    title: 'See what sells - and what to do next',
    lede: 'Revenue, orders, best-sellers and customer trends on one dashboard, computed from live data rather than yesterday’s export.',
    bullets: [
      'Sales, orders and AOV at a glance',
      'Best-sellers and slow movers ranked',
      'Customer growth and repeat rate over time',
    ],
    benefits: [
      {
        title: 'A morning-coffee dashboard',
        description:
          'The numbers that matter - today’s sales, pending orders, stock alerts - in the first screen you open.',
      },
      {
        title: 'Decisions, not just charts',
        description:
          'Spot the product to restock, the discount that worked and the channel that is quietly growing.',
      },
      {
        title: 'Deeper on bigger plans',
        description:
          'Advanced analytics adds cohort views and finer breakdowns as your data - and your questions - grow.',
      },
    ],
  },
  payments: {
    title: 'Every way India pays, in one checkout',
    lede: 'UPI, cards, netbanking, wallets and cash on delivery - powered by Razorpay, reconciled automatically and settled to your bank.',
    bullets: [
      'UPI, cards, netbanking and COD out of the box',
      'Totals computed server-side, to the paisa',
      'Automatic reconciliation with every order',
    ],
    benefits: [
      {
        title: 'Never lose a sale to a missing option',
        description:
          'The customer who only pays by UPI and the one who insists on COD both check out without friction.',
      },
      {
        title: 'Trustworthy by design',
        description:
          'Payments run on Razorpay’s PCI-compliant rails; your store never touches raw card details.',
      },
      {
        title: 'Books that balance',
        description:
          'Every payment is matched to its order and invoice automatically - month-end closes itself.',
      },
    ],
  },
  checkout: {
    title: 'A checkout built to finish the sale',
    lede: 'Fast, mobile-first and honest - clear totals, minimal fields and every payment method your customer expects. The last step of the sale is the one we obsess over most.',
    bullets: [
      'Optimised for one-handed mobile checkout',
      'Guest checkout with optional accounts',
      'Clear GST-inclusive totals, no surprises',
    ],
    benefits: [
      {
        title: 'Fewer fields, more orders',
        description:
          'Only what is needed to deliver and bill - returning customers reuse saved addresses in one tap.',
      },
      {
        title: 'Server-verified totals',
        description:
          'Prices, taxes and discounts are validated on the server at payment time, so the amount charged is always right.',
      },
      {
        title: 'Recover the almost-buyers',
        description:
          'Abandoned carts can be nudged back over WhatsApp with the cart intact - a second chance at every sale.',
      },
    ],
  },
  'gst-invoices': {
    title: 'GST compliance on autopilot',
    lede: 'Every order generates a GST-compliant invoice - correct rates, HSN codes and your GSTIN - numbered sequentially and ready for your CA.',
    bullets: [
      'Compliant invoice PDF for every order',
      'CGST/SGST/IGST split computed correctly',
      'Sequential numbering your auditor will like',
    ],
    benefits: [
      {
        title: 'Set it once, forget it',
        description:
          'Add your GSTIN and per-product tax rates; from then on every invoice is generated and stored automatically.',
      },
      {
        title: 'Right split, every state',
        description:
          'Intra-state and inter-state orders get the correct CGST/SGST or IGST treatment based on the delivery address.',
      },
      {
        title: 'Filing-ready records',
        description:
          'Download invoices in bulk for any period - GSTR filing season stops being a scramble.',
      },
    ],
  },
};

registerSiteContent('feature.pages', { FEATURE_CONTENT });
